import { Grid, Typography } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { ToolbarButton } from '@tassat/button';
import { Export as ExportIcon, FilterIcon, SortIcon } from '@tassat/icon';
import {
    DropdownMenu,
    FilterMenuItem,
    SearchField,
    SortMenuItem
} from '@tassat/menu';
import React from 'react';

import { GridSettingsContext } from 'SharedComponents/layouts/sideListLayout';
import { GridModelContext, SideListGridOptions } from 'SharedComponents/sideList/';

interface IGridToolbarProps extends WithStyles<typeof muiStyles> {
    id?: string;
    gridOptions: SideListGridOptions;
}

const muiStyles = () => ({
    root: { paddingRight: '.5rem' },
    title: {
        fontSize: '.8rem',
        marginLeft: '1rem',
        lineHeight: '1rem',
        alignSelf: 'flex-end'
    },
    buttons: { marginLeft: 'auto' as const },
    button: {
        width: '34px',
        height: '25px',
        marginLeft: '4px',
        border: '1px solid #80808059'
    },
    exportIcon: {
        height: '1.2rem',
        width: '0.8rem'
    },
    searchFieldRoot: { marginLeft: '4px' },
    searchIcon: {
        height: '12px',
        width: '12px'
    },
    icon: {
        width: '32px',
        height: '22px'
    },
    searchBarOpen: { width: '7rem' },
    exportButton: {
        width: '35px',
        marginLeft: '4px',
        borderColor: '#80808059'
    }
});

export function GridToolbarComponent(props: IGridToolbarProps) {
    const {
        classes,
        id,
        gridOptions
    } = props;

    const gridSettings = React.useContext(GridSettingsContext);
    const gridModel = React.useContext(GridModelContext);

    const [ sortMenuAnchor, setSortMenuAnchorEl ] = React.useState<null | HTMLElement>(null);
    const handleSortMenuClose = () => setSortMenuAnchorEl(null);

    const [ filterMenuAnchor, setFilterMenuAnchorEl ] = React.useState<null | HTMLElement>(null);
    const handleFilterMenuClose = () => setFilterMenuAnchorEl(null);

    const [ searchOpen, setSearchOpen ] = React.useState<boolean>(false);
    const toggleSearchBar = (open) => setSearchOpen(open);

    return (
        <Grid
            className={classes.root}
            container
            direction='row'
        >
            <Grid
                item
                xs={12}
            >
                <Grid
                    container
                    justifyContent='space-between'
                >
                    {gridSettings.title &&
                        <Grid
                            className={classes.title}
                            component={Typography}
                            item
                            variant='body1'
                        >
                            {gridSettings.title}
                        </Grid>}
                    <Grid
                        className={classes.buttons}
                        item
                    >
                        <Grid container>
                            {!searchOpen ?
                                <>
                                    {gridOptions.filterOptions && gridOptions.filterOptions.length > 0 &&
                                        <>
                                            <ToolbarButton
                                                active={Boolean(gridModel.filterModel)}
                                                aria-controls='filter-menu'
                                                aria-haspopup='true'
                                                className={classes.button}
                                                Icon={FilterIcon}
                                                id={`${id}_filterButton`}
                                                onClick={(e) => setFilterMenuAnchorEl(e.currentTarget)}
                                                text='Filter'
                                            />
                                            <DropdownMenu
                                                handleMenuClose={handleFilterMenuClose}
                                                id={`${id}_filterMenu`}
                                                menuAnchor={filterMenuAnchor}
                                                MenuItemComponent={FilterMenuItem}
                                                menuModel={{
                                                currentModel: gridModel.filterModel,
                                                options: gridOptions.filterOptions
                                            }}
                                                onSelect={gridModel.onFilter}
                                            />
                                        </>}
                                    {gridOptions.sortOptions && gridOptions.sortOptions.length > 0 &&
                                        <>
                                            <ToolbarButton
                                                active={Boolean(gridModel.sortModel)}
                                                aria-controls='sort-menu'
                                                aria-haspopup='true'
                                                className={classes.button}
                                                Icon={SortIcon}
                                                id={`${id}_sortButton`}
                                                onClick={(e) => setSortMenuAnchorEl(e.currentTarget)}
                                                text='Sort'
                                            />
                                            <DropdownMenu
                                                handleMenuClose={handleSortMenuClose}
                                                id={`${id}_sortMenu`}
                                                menuAnchor={sortMenuAnchor}
                                                MenuItemComponent={SortMenuItem}
                                                menuModel={{
                                                currentModel: gridModel.sortModel ?? gridOptions.sortModel,
                                                options: gridOptions.sortOptions
                                            }}
                                                onSelect={gridModel.onSort}
                                            />
                                        </>}
                                </> : null}
                            {gridOptions.searchByColumn && gridModel.onSearch &&
                                <SearchField
                                    classes={{
                                    root: classes.searchFieldRoot,
                                    searchIcon: classes.searchIcon,
                                    icon: classes.icon,
                                    searchBarOpen: classes.searchBarOpen
                                }}
                                    id={`${id}_searchButton`}
                                    isOpen={searchOpen}
                                    onSearch={gridModel.onSearch}
                                    searchValue={gridModel.searchValue}
                                    toggleSearchBar={toggleSearchBar}
                                />}
                            {gridOptions.exportData && !searchOpen &&
                                <ToolbarButton
                                    className={classes.exportButton}
                                    Icon={ExportIcon}
                                    iconClassName={classes.exportIcon}
                                    iconColor='#727272'
                                    id={`${id}_exportButton`}
                                    onClick={() => {
                                    if (gridOptions.exportDefaultArgs) {
                                        gridOptions.exportData(...gridOptions.exportDefaultArgs, gridModel.filterModel);
                                    } else {
                                        gridOptions.exportData(gridModel.filterModel);
                                    }
                                }}
                                    text='Export'
                                />}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}

const GridToolbar = withStyles(muiStyles)(GridToolbarComponent);
export { GridToolbar };